import React, { useState, useEffect, useRef } from 'react'
import WebsiteNav from './layouts/WebsiteNav'
import "./website.css"
export default function Website() {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' })
  const [errors, setErrors] = useState({})
  const [sent, setSent] = useState(false)
  const [testimonialIdx, setTestimonialIdx] = useState(0)
  const testimonials = [
    { text: '"xyleads helped us reduce missed calls by 40% in the first month. The reports are simple and powerful."', author: 'Priya, Operations Lead' },
    { text: '"Easy setup and our reps love the live call view. Exporting data for finance is a breeze."', author: 'Ahmed, Sales Manager' },
    { text: '"The team performance dashboards helped us coach agents more effectively and raise conversions."', author: 'Laura, Head of Support' }
  ]
  const carouselTimer = useRef(null)

  useEffect(() => {
    // auto-advance testimonials
    carouselTimer.current = setInterval(() => {
      setTestimonialIdx((i) => (i + 1) % testimonials.length)
    }, 4500)
    return () => clearInterval(carouselTimer.current)
  }, [])

  const validate = () => {
    const e = {}
    if (!form.name.trim()) e.name = 'Name is required'
    if (!form.email.trim()) e.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email'
    return e
  }

  const handleChange = (k) => (ev) => setForm((s) => ({ ...s, [k]: ev.target.value }))

  const handleSubmit = (ev) => {
    ev.preventDefault()
    const e = validate()
    setErrors(e)
    if (Object.keys(e).length === 0) {
      // simulate submit
      setSent(true)
      setTimeout(() => {
        // reset after a short delay to mimic async
        setForm({ name: '', email: '', company: '', message: '' })
      }, 800)
    }
  }

  return (
    <div className="main-bg overflow-x-hidden">
      <WebsiteNav />

      <header className="relative isolate">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            {/* Left: Hero content */}
            <div className="space-y-6">
              <span className="inline-block rounded-full bg-white bg-opacity-20 text-purple-200 px-4 py-2 text-sm font-medium backdrop-blur-sm border border-white border-opacity-30">
                Call analytics • Call intelligence • Insights
              </span>
              <h1 className="text-4xl overflow-hidden sm:text-5xl lg:text-6xl font-extrabold text-white ">
                Your reliable partner in <span className="gradient-text bg-gradient-to-r from-yellow-100 to-white bg-clip-text text-transparent">Call management</span>
              </h1>
              <p className="text-xl text-white text-opacity-90 max-w-xl leading-relaxed">
                xyleads provides enterprise-grade call analytics to help businesses and individuals optimize their communication strategies. Monitor conversations, measure outcomes, and improve agent performance with clear insights.
              </p>

              <ul className="mt-6 space-y-3 text-white text-opacity-90">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-white bg-opacity-20 flex items-center justify-center mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414L8.414 15l-4.121-4.121a1 1 0 111.414-1.414L8.414 12.172l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  Real-time call tracking and monitoring
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-white bg-opacity-20 flex items-center justify-center mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414L8.414 15l-4.121-4.121a1 1 0 111.414-1.414L8.414 12.172l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  Advanced call outcome and conversion reports
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-white bg-opacity-20 flex items-center justify-center mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414L8.414 15l-4.121-4.121a1 1 0 111.414-1.414L8.414 12.172l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  Secure, enterprise-grade platform
                </li>
              </ul>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:gap-4">
                <a href="#form" className="modern-btn text-center">
                  Request a demo
                </a>
                <a href="#learn" className="modern-btn-outline mt-3 sm:mt-0 text-center">
                  Learn how xyleads works
                </a>
              </div>
            </div>

            {/* Right: Card form + illustration */}
            <div className="mx-auto w-full max-w-lg">
              {/* <div className="mb-8 hidden md:block fade-in">
                <img src={heroSvg} alt="xyleads illustration" className="hero-illustration float-up" />
              </div> */}
              <div className="grid grid-cols-1 gap-4">
                {/* Form card */}
                <div className="glass-card p-8">
                  <h3 className="text-xl font-bold text-white mb-2">Start your free trial with xyleads</h3>
                  <p className="text-white text-opacity-80 mb-6">Analyze missed, incoming and outgoing calls. Filter by time — today, month or year and export reports.</p>

                  {sent ? (
                    <div className="rounded-xl bg-green-500 bg-opacity-20 backdrop-blur-sm border border-green-300 border-opacity-30 p-4 text-white font-medium">
                      Thanks — we'll reach out shortly.
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <input 
                          value={form.name} 
                          onChange={handleChange('name')} 
                          placeholder="Full name" 
                          className={`w-full rounded-xl bg-white bg-opacity-20 backdrop-blur-sm border px-4 py-3 text-white placeholder-white placeholder-opacity-70 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${errors.name ? 'border-red-300' : 'border-white border-opacity-30'}`} 
                        />
                        {errors.name && <p className="mt-2 text-sm text-red-200">{errors.name}</p>}
                      </div>

                      <div>
                        <input 
                          value={form.email} 
                          onChange={handleChange('email')} 
                          placeholder="Email address" 
                          className={`w-full rounded-xl bg-white bg-opacity-20 backdrop-blur-sm border px-4 py-3 text-white placeholder-white placeholder-opacity-70 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${errors.email ? 'border-red-300' : 'border-white border-opacity-30'}`} 
                        />
                        {errors.email && <p className="mt-2 text-sm text-red-200">{errors.email}</p>}
                      </div>
                        <div>
                        <input 
                          value={form.phone} 
                          onChange={handleChange('phone')} 
                          placeholder="Phone number" 
                          className={`w-full rounded-xl bg-white bg-opacity-20 backdrop-blur-sm border px-4 py-3 text-white placeholder-white placeholder-opacity-70 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${errors.phone ? 'border-red-300' : 'border-white border-opacity-30'}`} 
                        />
                        {errors.email && <p className="mt-2 text-sm text-red-200">{errors.email}</p>}
                      </div>

                      <div>
                        <input 
                          value={form.company} 
                          onChange={handleChange('company')} 
                          placeholder="Company (optional)" 
                          className="w-full rounded-xl bg-white bg-opacity-20 backdrop-blur-sm border border-white border-opacity-30 px-4 py-3 text-white placeholder-white placeholder-opacity-70 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50" 
                        />
                      </div>

                      <div>
                        <textarea 
                          value={form.message} 
                          onChange={handleChange('message')} 
                          rows={3} 
                          placeholder="How can we help? (optional)" 
                          className="w-full rounded-xl bg-white bg-opacity-20 backdrop-blur-sm border border-white border-opacity-30 px-4 py-3 text-white placeholder-white placeholder-opacity-70 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 resize-none" 
                        />
                      </div>

                      <button type="submit" className="w-full modern-btn bg-white text-purple-600 hover:bg-opacity-90">
                        Request Demo
                      </button>
                    </form>
                  )}

                  <p className="mt-6 text-xs text-white text-opacity-60 text-center">
                    We respect your privacy. Your information will only be used to contact you about xyleads.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="bg-transparent">
        {/* Features */}
        <section className="mx-auto max-w-7xl px-6 lg:px-8 py-20">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold gradient-text mb-4 overflow-hidden">Everything you need to manage calls</h2>
            <p className="text-lg text-gray-600">xyleads helps teams monitor missed, incoming and outgoing calls, analyze performance, and export reports by day, month or year.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="feature-card p-8">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-600">Real-time call monitoring</h3>
              <p className="text-gray-600">See calls as they happen and surface missed calls instantly so no lead is lost.</p>
            </div>

            <div className="feature-card p-8">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-green-500 to-teal-600 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-600">Reports & exports</h3>
              <p className="text-gray-600">Generate time-based reports (today / month / year) and export CSVs for deeper analysis.</p>
            </div>

            <div className="feature-card p-8">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-orange-500 to-red-600 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-600">Team performance</h3>
              <p className="text-gray-600">Track agent call volume, conversion and missed-call trends to improve coaching.</p>
            </div>

            <div className="feature-card p-8">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-5 5v-5zM3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-600">Alerts & integrations</h3>
              <p className="text-gray-600">Get notified for high-priority missed calls and integrate with CRMs or helpdesk tools.</p>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="bg-gray-50 py-20">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h3 className="text-3xl font-bold text-gray-600 overflow-hidden mb-4">How xyleads works</h3>
              <p className="text-lg text-gray-600">Set up in minutes — start tracking calls, view reports and act on missed opportunities.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="glass-card-white p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 text-white flex items-center justify-center text-2xl font-bold mx-auto mb-6">1</div>
                <h4 className="text-xl font-bold mb-4 text-gray-600">Connect</h4>
                <p className="text-gray-600">Connect your phone numbers or PBX to stream call logs securely.</p>
              </div>

              <div className="glass-card-white p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-green-500 to-teal-600 text-white flex items-center justify-center text-2xl font-bold mx-auto mb-6">2</div>
                <h4 className="text-xl font-bold mb-4 text-gray-600">Analyze</h4>
                <p className="text-gray-600">Automatically categorize incoming, outgoing and missed calls and surface trends.</p>
              </div>

              <div className="glass-card-white p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-orange-500 to-red-600 text-white flex items-center justify-center text-2xl font-bold mx-auto mb-6">3</div>
                <h4 className="text-xl font-bold mb-4 text-gray-600">Act</h4>
                <p className="text-gray-600">Export reports, alert teams about missed calls, and improve conversion rates.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="mx-auto max-w-7xl px-6 lg:px-8 py-20">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h3 className="text-3xl font-bold gradient-text overflow-hidden mb-4">Loved by teams across industries</h3>
            <p className="text-lg text-gray-200">Quick wins from customers using xyleads to manage call operations and boost conversions.</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="testimonial-carousel p-8">
              <div className="carousel-track" style={{ transform: `translateX(-${testimonialIdx * 100}%)` }}>
                {testimonials.map((t, i) => (
                  <div className="carousel-item text-center" key={i}>
                    <p className="text-xl text-gray-700 italic mb-6 leading-relaxed">"{t.text}"</p>
                    <div className="text-lg font-semibold gradient-text">— {t.author}</div>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex items-center justify-between">
                <div className="carousel-controls">
                  {testimonials.map((_, i) => (
                    <button key={i} onClick={() => setTestimonialIdx(i)} className={`carousel-dot ${testimonialIdx === i ? 'active' : ''}`} aria-label={`Go to testimonial ${i+1}`} />
                  ))}
                </div>
                <div className="flex gap-3">
                  <button className="carousel-nav" onClick={() => setTestimonialIdx((testimonialIdx - 1 + testimonials.length) % testimonials.length)}>← Prev</button>
                  <button className="carousel-nav" onClick={() => setTestimonialIdx((testimonialIdx + 1) % testimonials.length)}>Next →</button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Secondary CTA */}
        <section className="bg-gradient-to-r from-purple-600 via-blue-600 to-teal-600 text-white py-20">
          <div className="mx-auto max-w-7xl px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-2xl text-center md:text-left">
              <h4 className="text-3xl font-bold overflow-hidden mb-4">Ready to improve your call performance?</h4>
              <p className="text-lg text-white text-opacity-90">Start a free trial, or book a demo with our team to see xyleads in action.</p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4">
              <a href="#form" className="modern-btn bg-white text-purple-600 hover:bg-opacity-90">
                Request a demo
              </a>
              <a href="#" className="modern-btn-outline">
                Contact sales
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-white py-12">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 pb-8 border-b border-gray-200">
            <div className="text-center md:text-left">
              <h5 className="text-xl font-bold gradient-text mb-2">xyleads</h5>
              <p className="text-gray-600">Your reliable partner in call management</p>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <a href="#" className="text-gray-600 hover:text-purple-600 transition-colors">Privacy</a>
              <a href="#" className="text-gray-600 hover:text-purple-600 transition-colors">Terms</a>
              <a href="#" className="text-gray-600 hover:text-purple-600 transition-colors">Contact</a>
              <a href="#" className="text-gray-600 hover:text-purple-600 transition-colors">Support</a>
            </div>
          </div>
          <div className="pt-8 text-center text-gray-500">
            © {new Date().getFullYear()} xyleads. All rights reserved. Built with ❤️ for better call management.
          </div>
        </div>
      </footer>
    </div>
  )
}
